import {
  ConfigCondition,
  ConfigField,
  ConfigTab,
  ConfigValue,
  EnvironmentAdminConfig,
  EnvironmentFormState,
} from './adminConfig.types';

/** Fields that hold a value (banners are display-only). */
export const isValueField = (field: ConfigField): boolean => field.type !== 'banner' && !field.responseKey;

export const sortedTabs = (config: EnvironmentAdminConfig): ConfigTab[] =>
  [...config.tabs].sort((a, b) => a.order - b.order);

export const tabFields = (tab: ConfigTab): ConfigField[] => tab.sections.flatMap((section) => section.fields);

/** Every flat (dbKey) field of the section tabs, group children expanded. */
export const flatFields = (config: EnvironmentAdminConfig): Array<{ field: ConfigField; tab: ConfigTab }> =>
  config.tabs
    .filter((tab) => tab.apiSection)
    .flatMap((tab) =>
      tabFields(tab).flatMap((field) =>
        (field.type === 'group' ? field.children ?? [] : [field])
          .filter((leaf) => leaf.dbKey)
          .map((leaf) => ({ field: leaf, tab })),
      ),
    );

/** The value-carrying top-level fields of an application tab (no apiSection). */
export const applicationFields = (tab: ConfigTab): ConfigField[] => tabFields(tab).filter(isValueField);

export const fieldDefault = (field: ConfigField): ConfigValue => {
  if (field.type === 'group') {
    return Object.fromEntries((field.children ?? []).map((child) => [child.key, clone(child.default)]));
  }
  return clone(field.default);
};

export const clone = <T,>(value: T): T => (value === undefined ? value : JSON.parse(JSON.stringify(value)));

export type Lookup = (key: string) => { present: boolean; value: ConfigValue };

/**
 * Condition lookup for a tab: the tab's own application values first, then flat settings
 * (so an application tab can depend on `enable_compatio_apps`), then context values.
 */
export const makeLookup = (
  state: EnvironmentFormState,
  tab?: ConfigTab,
  responseValues: Record<string, ConfigValue> = {},
): Lookup => (key) => {
  if (tab && !tab.apiSection) {
    const values = state.applications[tab.id] ?? {};
    if (key in values) return { present: true, value: values[key] };
  }
  if (key in state.flat) return { present: true, value: state.flat[key] };
  if (key in responseValues) return { present: true, value: responseValues[key] };
  return { present: false, value: undefined };
};

export const evaluateCondition = (condition: ConfigCondition | undefined, lookup: Lookup): boolean => {
  if (!condition) return true;
  if (condition.all) return condition.all.every((item) => evaluateCondition(item, lookup));
  if (condition.any) return condition.any.some((item) => evaluateCondition(item, lookup));
  const { present, value } = lookup(condition.field ?? '');
  return present && value === condition.equals;
};

/** Where a field's value lives in the form state, as a path the panel can read and write. */
export const readValue = (state: EnvironmentFormState, tab: ConfigTab, field: ConfigField): ConfigValue =>
  tab.apiSection ? state.flat[field.dbKey ?? field.key] : state.applications[tab.id]?.[field.key];

export const writeValue = (
  state: EnvironmentFormState,
  tab: ConfigTab,
  field: ConfigField,
  value: ConfigValue,
): EnvironmentFormState => {
  if (tab.apiSection) {
    return { ...state, flat: { ...state.flat, [field.dbKey ?? field.key]: value } };
  }
  return {
    ...state,
    applications: { ...state.applications, [tab.id]: { ...(state.applications[tab.id] ?? {}), [field.key]: value } },
  };
};

/**
 * Apply `mutuallyExclusiveWith` and dependent `enabledWhen` toggles after a change: switching a
 * toggle on switches its exclusive siblings off, and a toggle whose `enabledWhen` no longer holds
 * is switched off, so the save never carries a combination the backend rejects.
 */
export const applyDependencies = (
  state: EnvironmentFormState,
  tab: ConfigTab,
  changed: ConfigField,
  value: ConfigValue,
): EnvironmentFormState => {
  let next = state;
  if (value === true) {
    for (const otherKey of changed.mutuallyExclusiveWith ?? []) {
      const other = tabFields(tab).find((field) => field.key === otherKey);
      if (other && readValue(next, tab, other) === true) next = writeValue(next, tab, other, false);
    }
  }
  for (const field of tabFields(tab)) {
    if ((field.type === 'toggle' || field.type === 'checkbox') && field.enabledWhen && readValue(next, tab, field) === true) {
      if (!evaluateCondition(field.enabledWhen, makeLookup(next, tab))) next = writeValue(next, tab, field, false);
    }
  }
  return next;
};

export const fillMessage = (template: string, values: Record<string, ConfigValue>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), template);
